/** The words and the arithmetic of Home - pure, so they are tested without a DOM.
 *
 * **Codes in, sentences out, and the sentences are ours.** The summary endpoint sends `next_step`,
 * `verdict` and `unavailable_reason` as codes precisely so that this screen, and not a server
 * string, decides how a filing's state is said (`docs/design/api-wave3/b1-b4-summary-approvals.md`).
 * Nothing here states a regulation: a regime is named by its key, a date is the server's, and a
 * date in a regime that is not yet law (`in_force_caveat`) is never counted down to (C-03, C-07).
 *
 * **Null is not zero** - a count a role is not asked is `null` and stays null through every
 * function here, so the spine draws "not asked" and never "0".
 */

import type { components } from "../api/schema";
import { dueStatus } from "./dueStatus";
import { countsDown, dateStillMatters, isIssuedState, issuedButOwing, stateWords } from "./filingState";
import { daysBetween } from "./readinessModel";

type FilingRow = components["schemas"]["FilingRowOut"];
type Counts = components["schemas"]["SummaryCountsOut"];
type WorkItem = components["schemas"]["WorkItemOut"];

export function filingTitle(row: Pick<FilingRow, "regime_key">): string {
  return row.regime_key.replace(/_/g, " ");
}

/** When a filing is due, in words. A regime that is not law yet gets no countdown, and a filing
 * with no date says why it has none (`due_date_basis` is never empty). The words for the distance are
 * the ones `dueStatus` gives, the one vocabulary Home, the Filings list and the dashboards share (U3). */
export function duePhrase(row: FilingRow): string {
  if (row.in_force_caveat) return "has a proposed date and no countdown";
  // An issued filing has nothing left to be late for (G-P5-10): the Executive dashboard said "is due in
  // 7 days" beside the Issued chip. The date belongs to the issue, so the sentence is about the issue.
  if (isIssuedState(row.state)) return "was issued";
  const status = dueStatus(row.days_left);
  switch (status.kind) {
    case "none":
      return "has no due date set";
    case "overdue":
      return `is ${status.overdue ?? ""}`;
    case "today":
      return "is due today";
    default:
      return `is due ${status.ahead ?? ""}`;
  }
}

/** True for a regime that is not operative law (SB 261, enjoined; a proposed date): both fields are
 * set together by the server, and either one is enough to know the row is not a live duty. */
export function isLive(row: Pick<FilingRow, "in_force_caveat" | "in_force_label">): boolean {
  return !row.in_force_caveat && !row.in_force_label;
}

export interface DueRead {
  /** The live filings whose date has passed, oldest first. */
  overdue: FilingRow[];
  /** The soonest live filing whose date is today or ahead. */
  next: FilingRow | undefined;
}

/** The live filings still to do, split by the server's `days_left` (U3): the dates already gone, oldest
 * first, and the soonest one still ahead. A regime that is not yet law is not counted down to, and an
 * issued filing is done. **A date that has passed is not "the next filing due"**: Home used to print
 * Sep 30 as the next date on 2 November with no word that it was 33 days gone. */
export function readDue(rows: readonly FilingRow[]): DueRead {
  const todo = rows
    .filter((row) => isLive(row) && dateStillMatters(row.state) && row.due_date)
    .sort((a, b) => (a.due_date ?? "").localeCompare(b.due_date ?? ""));
  const kind = (row: FilingRow) => dueStatus(row.days_left).kind;
  return {
    overdue: todo.filter((row) => kind(row) === "overdue"),
    next: todo.find((row) => kind(row) === "today" || kind(row) === "ahead"),
  };
}

/** Why the due-date tile has no date, in a few words, or `undefined` when there is nothing to say
 * (no live filing at all). A bare dash read as a failed read (G-REG-02). Every live filing finished
 * and issued is one reason; a live filing with no date is the other. */
export function noDueReason(rows: readonly FilingRow[]): string | undefined {
  const live = rows.filter(isLive);
  if (live.length === 0) return undefined;
  if (live.every((row) => isIssuedState(row.state) && !issuedButOwing(row.state))) return "All issued";
  if (live.some((row) => dateStillMatters(row.state) && !row.due_date)) return "No date set";
  return undefined;
}

/** The filing the hero speaks about, **live law first** (ADR-0199, defect C8 of the US walkthrough):
 * a regime that is enjoined or only proposed never leads a customer who has a live duty, whatever
 * its checks say, because an enjoined filing with no date was leading a customer with a ten-day
 * deadline. Within live law the soonest filing with something still to do (the server sorts soonest
 * first, undated last; an issued filing whose figures moved or whose approval lapsed still has
 * something to do); then a live filing already issued (it is the live duty, and it is done); and only
 * when no live duty exists the same order over the rest. `undefined` for no filings. */
export function focusFiling(filings: readonly FilingRow[]): FilingRow | undefined {
  const live = filings.filter(isLive);
  const rest = filings.filter((row) => !isLive(row));
  const open = (row: FilingRow) => !isIssuedState(row.state) || issuedButOwing(row.state);
  return live.find(open) ?? live[0] ?? rest.find(open) ?? rest[0];
}

export type HeroTone = "crit" | "warn" | "ok" | "neutral";

export interface Hero {
  /** "UK SECR is 31 days overdue." */
  lead: string;
  /** What stands in the way, or what happens next. */
  detail: string;
  tone: HeroTone;
}

const UNAVAILABLE: Record<string, string> = {
  boundary_rules_not_verified:
    "Its boundary rules are not verified yet, so its checks cannot be shown.",
  context_unavailable: "Its context could not be built, so its checks cannot be shown.",
  regime_unavailable: "Its rules are not loaded, so its checks cannot be shown.",
};

function blockingWords(count: number): string {
  return `${count} blocking ${count === 1 ? "check" : "checks"} to fix.`;
}

/** What is owed on a filing that was issued and is owed something again, by the server's next step. */
const NEXT_AFTER_ISSUE: Readonly<Record<string, string>> = {
  fix_checks: "Its checks need fixing.",
  request_approval: "It needs sending for approval.",
  wait_for_reviewer: "It is waiting on a reviewer.",
  approve: "It is waiting for your approval.",
  adjust: "A reviewer asked for an adjustment.",
  issue: "It can be issued again.",
};

/** The one sentence Home leads with. Chosen by the filing's `next_step`, which is the server's
 * ordered rule (checks decide before approvals do), so the wording cannot contradict the verdict. */
export function heroFor(filings: readonly FilingRow[]): Hero {
  const row = focusFiling(filings);
  if (!row) {
    return {
      lead: "No filing is confirmed as owed for this period.",
      detail: "Filings appear here once the company's facts are stated and a position is determined.",
      tone: "neutral",
    };
  }
  /* An enjoined or proposed regime is not counted among what is owed beside a live duty. */
  const others = filings.filter((other) => other !== row && (isLive(other) || !isLive(row))).length;
  const lead = `${filingTitle(row)} ${duePhrase(row)}.`;
  const extra = others > 0 ? ` ${others} other ${others === 1 ? "filing is" : "filings are"} also owed.` : "";
  const late =
    row.days_left !== null && row.days_left < 0 && !row.in_force_caveat && countsDown(row.state);
  if (!row.available) {
    return {
      lead,
      detail: (UNAVAILABLE[row.unavailable_reason ?? ""] ?? "Its checks cannot be shown.") + extra,
      tone: "warn",
    };
  }
  if (issuedButOwing(row.state)) {
    // Said from the facts the server sent, then the one thing to do (the state and the next step are
    // both the server's, so this cannot contradict the Filings list or the filing's own page).
    return {
      lead,
      detail: `${stateWords(row.state).word}. ${NEXT_AFTER_ISSUE[row.next_step] ?? ""}`.trim() + extra,
      tone: "warn",
    };
  }
  switch (row.next_step) {
    case "fix_checks": {
      const blocking = row.blocking_findings ?? 0;
      return {
        lead,
        detail: (blocking > 0 ? blockingWords(blocking) : "Its checks are not passing yet.") + extra,
        tone: late || blocking > 0 ? "crit" : "warn",
      };
    }
    case "request_approval":
      return { lead, detail: "Its checks have passed. It needs sending for approval." + extra, tone: late ? "crit" : "neutral" };
    case "wait_for_reviewer":
      return {
        lead,
        detail: `It is waiting on ${row.approval?.stage_name ?? "a reviewer"}.` + extra,
        tone: late ? "crit" : "neutral",
      };
    case "approve":
      return { lead, detail: "It is waiting for your approval." + extra, tone: late ? "crit" : "warn" };
    case "adjust":
      return { lead, detail: "A reviewer asked for an adjustment." + extra, tone: "warn" };
    case "issue":
      return { lead, detail: "It is approved and ready to issue." + extra, tone: late ? "crit" : "warn" };
    case "view_issued":
      return { lead, detail: "It has been issued." + extra, tone: "ok" };
    default:
      return { lead, detail: "Its checks have not been run yet." + extra, tone: "neutral" };
  }
}

export interface HeroAction {
  label: string;
  /** Where it goes; `undefined` when the role cannot take this step. */
  to: "/reports" | "/approvals" | undefined;
  /** Why a role that cannot is told, shown beside a disabled control. */
  reason: string | undefined;
}

/** The hero's one action, by `next_step`. A step the caller cannot take is returned with `to`
 * undefined and the reason, so the screen draws it disabled with the words (v5-build-scope, section
 * 1) - never hidden and never active. */
export function heroAction(row: FilingRow | undefined): HeroAction | undefined {
  if (!row) return undefined;
  const can = row.you_can;
  switch (row.next_step) {
    case "fix_checks":
      return { label: "Fix the checks", to: "/reports", reason: undefined };
    case "request_approval":
      return can
        ? { label: "Send for approval", to: "/reports", reason: undefined }
        : { label: "Send for approval", to: undefined, reason: "Only an Admin, an Approver or a Contributor can send a filing for approval." };
    case "approve":
      return can
        ? { label: "Review and approve", to: "/approvals", reason: undefined }
        : { label: "Review and approve", to: undefined, reason: "Only the reviewer named for this stage can approve." };
    case "wait_for_reviewer":
      return { label: "See where it is", to: "/reports", reason: undefined };
    case "adjust":
      return can
        ? { label: "Make the adjustment", to: "/reports", reason: undefined }
        : { label: "Make the adjustment", to: undefined, reason: "Only the person who submitted it can adjust it." };
    case "issue":
      return can
        ? { label: "Issue the filing", to: "/reports", reason: undefined }
        : { label: "Issue the filing", to: undefined, reason: "Only an Admin or an Approver can issue a filing." };
    case "view_issued":
      return { label: "View the issued filing", to: "/reports", reason: undefined };
    default:
      return { label: "Open the filing", to: "/reports", reason: undefined };
  }
}

// ------------------------------------------------------------------------------------------ spine

export type SpineKey = "collect" | "review" | "ready" | "approve" | "issue";
export type SpineState = "done" | "current" | "ahead" | "unknown";

export interface SpineStep {
  key: SpineKey;
  label: string;
  /** The figure under the label, or "-" when this role is not asked. */
  value: string;
  note: string;
  to: "/sources" | "/evidence" | "/readiness" | "/approvals" | "/reports";
  state: SpineState;
}

/** The five stages of getting a filing out, from the summary's own counts. **No percentage**: there
 * is no such quantity, and a ring the screen drew would be a degree of readiness nobody measured.
 * Each stage is "N things outstanding" or "not asked of this role"; the *current* stage is the first
 * with something outstanding, and a stage the role is not asked about (null) is never current. */
export function spineOf(counts: Counts, filings: readonly FilingRow[]): SpineStep[] {
  const inApproval = filings.filter(
    (row) => row.next_step === "approve" || row.next_step === "wait_for_reviewer",
  ).length;
  // Finished means issued **and** nothing owed again: a filing issued whose approval lapsed or whose
  // figures moved is not counted as done (G-Wauditor-02).
  const issued = filings.filter((row) => row.state.state === "issued").length;
  const outstanding = (n: number | null) => (n === null ? null : n);
  const raw: Array<Omit<SpineStep, "state"> & { open: number | null }> = [
    {
      key: "collect",
      label: "Collect",
      value: counts.missing_source_windows === null ? "-" : String(counts.missing_source_windows),
      note: counts.missing_source_windows === null ? "not shown to your role" : "bills missing",
      to: "/sources",
      open: outstanding(counts.missing_source_windows),
    },
    {
      key: "review",
      label: "Review",
      value: counts.documents_to_review === null ? "-" : String(counts.documents_to_review),
      note: counts.documents_to_review === null ? "not shown to your role" : "documents to read",
      to: "/evidence",
      open: outstanding(counts.documents_to_review),
    },
    {
      key: "ready",
      label: "Ready",
      value: counts.blocking_findings === null ? "-" : String(counts.blocking_findings),
      note: counts.blocking_findings === null ? "no filing could be checked" : "blocking checks",
      to: "/readiness",
      open: outstanding(counts.blocking_findings),
    },
    {
      key: "approve",
      label: "Approve",
      value: String(inApproval),
      note: inApproval === 1 ? "filing in approval" : "filings in approval",
      to: "/approvals",
      open: inApproval,
    },
    {
      key: "issue",
      label: "Issue",
      value: String(issued),
      note: `of ${filings.length} ${filings.length === 1 ? "filing" : "filings"} issued`,
      to: "/reports",
      open: filings.length > issued ? filings.length - issued : 0,
    },
  ];
  const currentAt = raw.findIndex((step) => step.open !== null && step.open > 0);
  return raw.map(({ open, ...step }, at) => ({
    ...step,
    state:
      open === null
        ? "unknown"
        : currentAt === -1 || at < currentAt
          ? "done"
          : at === currentAt
            ? "current"
            : "ahead",
  }));
}

// -------------------------------------------------------------------------------------- needs you

export type UrgencyGroup = "overdue" | "week" | "later";

export const URGENCY_LABEL: Record<UrgencyGroup, string> = {
  overdue: "Overdue",
  week: "This week",
  later: "Later",
};

/** Which group a work item belongs to, against `today` (an ISO date). The server's `overdue` flag
 * wins; otherwise the item's own due date. An undated item is Later: it has no clock to be on. */
export function urgencyOf(item: Pick<WorkItem, "overdue" | "due">, today: string): UrgencyGroup {
  if (item.overdue) return "overdue";
  if (!item.due) return "later";
  const left = daysBetween(today, item.due);
  if (left === undefined) return "later";
  if (left < 0) return "overdue";
  return left <= 7 ? "week" : "later";
}

export interface ItemAction {
  label: string;
  /** A path inside the app. Never built from free text: only from ids the server issued. */
  to: string;
  /** Search parameters the path needs (a filing to open), again only from ids the server issued. */
  search?: Record<string, string>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const idOf = (value: string | undefined): string | undefined =>
  value !== undefined && UUID.test(value) ? value : undefined;

/** Where a work item's action goes, by what the server says to do (`fix.action`) and the ids it
 * names. An id that is not a uuid is not followed: the fallback is the list the item belongs to. */
export function actionFor(item: WorkItem): ItemAction {
  const target = item.fix?.target ?? {};
  switch (item.fix?.action) {
    case "upload_document":
      return { label: "Add the data", to: "/evidence" };
    case "assign_collector": {
      const source = idOf(target.expected_source_id);
      return { label: "Name who collects it", to: source ? `/sources/${source}` : "/sources" };
    }
    case "declare_nil": {
      const source = idOf(target.expected_source_id);
      return { label: "Declare or add", to: source ? `/sources/${source}` : "/sources" };
    }
    case "verify_document": {
      const document = idOf(target.document_id);
      return { label: "Review the document", to: document ? `/evidence/${document}` : "/evidence" };
    }
    case "record_lease": {
      const site = idOf(target.facility_id);
      return { label: "Answer the question", to: site ? `/organisation/sites/${site}` : "/organisation" };
    }
    case "explain_difference":
      return { label: "Explain the difference", to: "/prior-disclosures" };
    case "chase_supplier":
      return { label: "Chase the supplier", to: "/suppliers" };
    case "decide_approval":
      return { label: "Decide", to: "/approvals" };
    case "request_approval": {
      // A filing waiting to be sent (U2) opens on the filing, where its to-do list and the send are.
      const filing = idOf(target.obligation_id);
      return filing
        ? { label: "Send for approval", to: "/reports", search: { filing } }
        : { label: "Send for approval", to: "/reports" };
    }
    default: {
      const record = idOf(item.record_id ?? undefined);
      return { label: "Open", to: record ? `/records/${record}` : "/tasks" };
    }
  }
}

/** What a filing needs next, in two or three words, for a row of the Filings list. */
export function stepLabel(row: FilingRow): string {
  if (!row.available) return "Cannot be checked";
  if (issuedButOwing(row.state)) return stateWords(row.state, { compact: true }).word;
  switch (row.next_step) {
    case "fix_checks":
      return "Fix the checks";
    case "request_approval":
      return "Ready for approval";
    case "wait_for_reviewer":
      return `Waiting on ${row.approval?.stage_name ?? "a reviewer"}`;
    case "approve":
      return "Needs approval";
    case "adjust":
      return "Adjustment asked";
    case "issue":
      return "Ready to issue";
    case "view_issued":
      return "Issued";
    default:
      return "Not evaluated";
  }
}
